/**
 * ModelCard - 广场卡片（统一网格·信息丰富）。
 *
 * 布局：
 *   ┌──────────────────────┐
 *   │ 标题（浮在缩略图左上） │
 *   │      官方精选徽标(右上)│
 *   │ [缩略图]              │
 *   ├──────────────────────┤
 *   │ 标签          分类    │ ← 标签行
 *   │ ●作者名      ❤N ⬇N   │ ← 作者行
 *   └──────────────────────┘
 *
 * 点击行为：
 *   - 传 onOpen → 渲染为 div（role=button），就地打开预览弹窗（不跳转）。
 *   - 未传 onOpen → 渲染为 LocalizedLink，跳转详情页（admin/直链入口）。
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PlazaModelSummary } from "../lib/api";
import { LocalizedLink } from "../lib/lang";
import { Heart, Download, Image as ImageIcon } from "./icons";

/** 广场摘要的 tags 是 JSON string，解析为 string[]（损坏/空 → []） */
function parseTags(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((t): t is string => typeof t === "string")
      : [];
  } catch {
    return [];
  }
}

export default function ModelCard({
  model,
  linkTo,
  onOpen,
  onHover,
  priority = false,
}: {
  model: PlazaModelSummary;
  /** 卡片点击跳转路径，缺省为公开广场 /plaza/:id（无语言前缀，LocalizedLink 自动补） */
  linkTo?: string;
  /** 传入则就地打开预览弹窗（不跳转）；不传则跳转详情页 */
  onOpen?: (model: PlazaModelSummary) => void;
  /** 桌面 hover 时触发（用于预取 GLB）；移动端不会触发（无 hover 能力） */
  onHover?: (model: PlazaModelSummary) => void;
  /** 首屏可见的卡片传 true：缩略图 eager + fetchpriority=high（lazy 会被浏览器
   *  降优先级，首屏缩略图恰是落地页的 LCP 元素——PSI mobile 实测瓶颈） */
  priority?: boolean;
}) {
  const { t } = useTranslation();
  const [imgError, setImgError] = useState(false);
  const categoryLabel =
    model.category ? t(`common.category.${model.category}`) : null;
  // 最多展示 2 个标签胶囊
  const tags = parseTags(model.tags).slice(0, 2);

  // 缩略图区（标题浮层 + 授权徽标），两种渲染模式共用
  const thumb = (
    <div className="model-card-thumb">
      {model.sourceImageR2Key && !imgError ? (
        <img
          src={`/api/plaza/${model.id}/thumb`}
          alt={model.title}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          decoding={priority ? "sync" : "async"}
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="model-card-placeholder" aria-hidden>
          <ImageIcon size={28} />
        </span>
      )}
      <div className="model-card-title-overlay">
        {/* h3：卡片标题。页面层级为 h1(页面)→h2(区块)→h3(卡片)，顺次不跳级
            （axe heading-order）；字号由 .model-card-title 控制，与标签无关 */}
        <h3 className="model-card-title">{model.title}</h3>
      </div>
      <div className="model-card-badges">
        {model.isSeed && (
          <span className="seed-badge">{t("plaza.officialPick")}</span>
        )}
      </div>
    </div>
  );

  // 信息区（分类行 + 作者行），两种渲染模式共用
  const info = (
    <div className="model-card-info">
      <div className="model-card-meta">
        {tags.length > 0 && (
          <div className="model-card-tags">
            {tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        {categoryLabel && (
          <span className="model-card-category">{categoryLabel}</span>
        )}
      </div>
      <div className="model-card-author-row">
        <div className="model-card-author">
          <span className="model-card-author-avatar" aria-hidden>
            {(model.authorName || "?").charAt(0).toUpperCase()}
          </span>
          <span className="model-card-author-name">{model.authorName}</span>
        </div>
        <span className="model-card-stats">
          <span title={t("plaza.likes")}>
            <Heart size={12} /> {model.likes}
          </span>
          <span title={t("plaza.downloads")}>
            <Download size={12} /> {model.downloads}
          </span>
        </span>
      </div>
    </div>
  );

  // 就地预览模式：div + role=button
  if (onOpen) {
    return (
      <div
        className="model-card"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(model)}
        onMouseEnter={onHover ? () => onHover(model) : undefined}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(model);
          }
        }}
      >
        {thumb}
        {info}
      </div>
    );
  }

  // 跳转模式：LocalizedLink（保持原有行为）
  return (
    <LocalizedLink to={linkTo ?? "/plaza"} className="model-card">
      {thumb}
      {info}
    </LocalizedLink>
  );
}
